import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  Injector, OnDestroy
} from '@angular/core';
import { ScreenType } from "../../../model/ScreenType";
import { UpaService, UpaTarget } from "../../../services/upa.service";
import { AbstractComponent } from "../../abstract.component";
import { VideoData, VideoPlayerService } from 'projects/common/services/video-player.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'video-player',
  template: `
      <div class="video-player-wrapper" (click)="close()">
        <div class="video-container" [ngClass]="{'has-button': videoData.video.link}">
          <ul class="eva-3-nav-dots -eva-3-mt-xl -eva-3-mb-xl" [style.--progress]="progress">
            <li *ngFor="let videoItem of videoData.video.videos; let i = index" role="presentation"
                class="nav-dot" [ngClass]="{'-active': i <= currentVideoIndex, '-playing': i === currentVideoIndex}"
                (click)="this.setCurrentSource(i, $event)"></li>
          </ul>
          <div *ngIf="loading" class="video-skeleton"
              [ngStyle]="{'width': videoWidth + 'px', 'height': videoWidth * 16/9 + 'px'}">
            <div class="video-skeleton-half -top"></div>
            <div class="video-skeleton-half -bottom"></div>
          </div>
          <div>
            <miniature [video]="videoData.video" [miniatureWidth]="32" class="-eva-3-bold"></miniature>
            <div *ngIf="videoData.video.hashtag" class="hashtag eva-3-h6 -eva-3-halign -eva-3-valign">{{videoData.video.hashtag}}</div>
          </div>
          <div class="controls">
            <span (click)="toggleVideo($event)" class="playPause eva-3-icon-{{videoData.playing ? 'pause' : 'play'}} -eva-3-mr-md eva-3-link"></span>
            <span (click)="close()" class="close eva-3-icon-cross eva-3-link"></span>
          </div>

          <video #videoPlayer [width]="videoWidth" [height]="videoHeight" (ended)="videoEnded()"
                (loadstart)="this.loading = true" (loadeddata)="this.loading = false"></video>

          <div class="link-button -eva-3-halign -eva-3-pb-lg">
            <eva-button *ngIf="videoData.video.link" class="button-container"
                        [options]="{ size: '-lg', type: '-white', link: videoData.video.link, link_text: link_text }"
                        [moduleId]="videoData.trackingData.moduleId"
                        [moduleIndex]="videoData.trackingData.moduleIndex"
                        [uniqueId]="videoData.trackingData.uniqueId"></eva-button>
          </div>
          <div class="left-overlay" (click)="this.setCurrentSource(this.currentVideoIndex - 1, $event)"></div>
          <div class="right-overlay" (click)="this.setCurrentSource(this.currentVideoIndex + 1, $event)"></div>
        </div>
      </div>
  `,
  styleUrls: ['./video-player.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VideoPlayerComponent extends AbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('videoPlayer', { read: ViewContainerRef, static: true }) videoPlayer: ViewContainerRef;

  private static MODULE_ID: string = "VideoPlayerComponent";

  videoWidth: number;
  videoHeight: number;
  currentVideoIndex: number = 0;
  loading: boolean = true;
  progress: number = 0;
  progressInterval;
  link_text?: string;

  @Input() override screenType: ScreenType;
  @Input() videoData: Required<VideoData>;

  constructor(private videoPlayerService: VideoPlayerService,
              private translateService: TranslateService,
              injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this.videoWidth = this.resolveVideoWidth();
    this.videoHeight = this.resolveVideoHeight();
    this.link_text = this.videoData.video.link_text ?? this.translateService.instant('videoPlayer.viewMore');
    this.calculateProgress();
  }

  ngAfterViewInit() {
    this.load();
    this.calculateProgress();
  }

  toggleVideo(event: any) {
    event.stopPropagation();

    let video = this.videoPlayer.element.nativeElement;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
    this.videoData.playing = !video.paused;
  }

  videoEnded() {
    if (this.currentVideoIndex < this.videoData.video!.videos.length - 1) {
      this.setCurrentSource(this.currentVideoIndex + 1);
    } else {
      const { uniqueId, moduleId } = this.videoData.trackingData;
      this.upaService.trackClickTopic({
        label: "finishedAllStories",
        moduleId,
        uniqueId,
        moduleIndex: this.currentVideoIndex,
        target: UpaTarget.VIDEO,
      });
      this.videoData.playing = false;
      this.setCurrentSource(0);
      this.close();
    }
  }

  setCurrentSource(index: number, event?: MouseEvent) {
    if (event) { event.stopPropagation(); }
    if (index >= 0 && this.videoData && index <= this.videoData.video.videos.length - 1) {
      this.currentVideoIndex = index;
      const { uniqueId, moduleId } = this.videoData.trackingData;
      this.upaService.trackClickTopic({
        label: "navigateVideo",
        moduleId,
        uniqueId,
        moduleIndex: this.currentVideoIndex,
        target: UpaTarget.VIDEO,
      });
      this.load();
    }
  }

  private load() {
    const video = this.videoPlayer.element.nativeElement;
    video.src = '//media.staticontent.com/media/documents/' + this.videoData.video.videos[this.currentVideoIndex];
    video.load();
    if (this.videoData.playing) video.play();
  }

  close() {
    this.videoPlayerService.close({
      moduleId: this.getModuleId(),
      moduleIndex: this.videoData.trackingData.moduleIndex,
      uniqueId: this.videoData.trackingData.uniqueId,
    });
  }

  private resolveVideoWidth() {
    return this.screenType === 'DESKTOP' ? (window.innerWidth / 4) : window.innerWidth;
  }

  private resolveVideoHeight() {
    return this.screenType === 'DESKTOP' ? (this.videoWidth * 16 / 9) : window.innerHeight;
  }

  calculateProgress() {
    const video = this.videoPlayer.element.nativeElement;
    this.progress = video.currentTime / video.duration || 0;
    this.progressInterval = requestAnimationFrame(() => this.calculateProgress());
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.progressInterval);
  }

  getModuleId() {
    return VideoPlayerComponent.MODULE_ID;
  }
}
